import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { apiRequest } from '../utils/api'
import { ArrowLeft, Ban, CheckCircle, Shield, Edit2, Trash2, X, Save, KeyRound, Users, FileText, Calendar } from 'lucide-react'
import PageHeader from '../components/admin/PageHeader'
import StatCard from '../components/admin/StatCard'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog'

export default function UserDetail() {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [saveLoading, setSaveLoading] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState(null)

  useEffect(() => { loadUser() }, [userId])

  const loadUser = async () => {
    try {
      const userData = await apiRequest(`/api/admin/users/${userId}`)
      setData(userData)
      setEditForm({
        name: userData.user.name || '',
        email: userData.user.email || '',
        phone: userData.user.phone || '',
        subscription_plan: userData.user.subscription?.plan || 'free'
      })
    } catch (err) { console.error('Error loading user:', err) }
    finally { setLoading(false) }
  }

  const withConfirm = (message, action) => {
    setConfirmDialog({ message, action })
  }

  const executeConfirmed = async () => {
    const action = confirmDialog?.action
    setConfirmDialog(null)
    if (action) await action()
  }

  const handleSuspend = () => withConfirm('Are you sure you want to suspend this user?', async () => {
    setActionLoading(true)
    try { await apiRequest(`/api/admin/users/${userId}/suspend`, { method: 'POST' }); loadUser() }
    catch (err) { alert(err.message) } finally { setActionLoading(false) }
  })

  const handleUnsuspend = async () => {
    setActionLoading(true)
    try { await apiRequest(`/api/admin/users/${userId}/unsuspend`, { method: 'POST' }); loadUser() }
    catch (err) { alert(err.message) } finally { setActionLoading(false) }
  }

  const handleMakeAdmin = () => withConfirm('Grant admin privileges to this user?', async () => {
    setActionLoading(true)
    try { await apiRequest(`/api/admin/users/${userId}/make-admin`, { method: 'POST' }); loadUser() }
    catch (err) { alert(err.message) } finally { setActionLoading(false) }
  })

  const handleDelete = () => withConfirm('Are you sure you want to delete this user? This action cannot be undone.', async () => {
    setActionLoading(true)
    try { await apiRequest(`/api/admin/users/${userId}`, { method: 'DELETE' }); alert('User deleted successfully'); navigate('/users') }
    catch (err) { alert('Error deleting user: ' + err.message); setActionLoading(false) }
  })

  const handleSendPasswordReset = () => withConfirm('Send a password reset email to this user?', async () => {
    setActionLoading(true)
    try { const result = await apiRequest(`/api/admin/users/${userId}/send-password-reset`, { method: 'POST' }); alert(`Password reset email sent to ${result.email}`) }
    catch (err) { alert(err.message) } finally { setActionLoading(false) }
  })

  const handleSaveEdit = async () => {
    setSaveLoading(true)
    try { await apiRequest(`/api/admin/users/${userId}`, { method: 'PUT', body: JSON.stringify(editForm) }); setEditMode(false); loadUser() }
    catch (err) { alert(err.message) } finally { setSaveLoading(false) }
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-2 border-indigo-600 border-t-transparent"></div></div>
  }

  if (!data) return <div className="text-center py-12 text-gray-500">User not found</div>

  const { user, teams, players, stats, invoices } = data

  return (
    <div className="space-y-6">
      <Link to="/users" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" /> Back to Users
      </Link>

      {/* User Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <span className="text-gray-700 text-2xl font-bold">{user.name?.charAt(0) || '?'}</span>
              )}
            </div>
            <div className="flex-1">
              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input type="tel" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} placeholder="Optional" />
                  </div>
                  <div>
                    <Label>Subscription Plan</Label>
                    <Select value={editForm.subscription_plan} onValueChange={(val) => setEditForm({...editForm, subscription_plan: val})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="coach">Coach</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveEdit} disabled={saveLoading}><Save className="w-4 h-4 mr-2" />{saveLoading ? 'Saving...' : 'Save Changes'}</Button>
                    <Button variant="outline" onClick={() => setEditMode(false)}><X className="w-4 h-4 mr-2" />Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
                    {user.is_admin && <Badge className="bg-gray-900 text-white">Admin</Badge>}
                    {user.is_suspended && <Badge className="bg-rose-100 text-rose-700">Suspended</Badge>}
                  </div>
                  <p className="text-gray-500">{user.email}</p>
                  {user.phone && <p className="text-gray-500">{user.phone}</p>}
                  <p className="text-sm text-gray-400 mt-1">Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</p>
                  <div className="mt-2">
                    <Badge className={
                      user.subscription?.plan === 'premium' ? 'bg-indigo-100 text-indigo-700' :
                      user.subscription?.plan === 'coach' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                    }>{user.subscription?.plan || 'Free'} Plan</Badge>
                  </div>
                </>
              )}
            </div>
            {!editMode && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setEditMode(true)}><Edit2 className="w-4 h-4 mr-2" />Edit</Button>
                {user.is_suspended ? (
                  <Button onClick={handleUnsuspend} disabled={actionLoading}><CheckCircle className="w-4 h-4 mr-2" />Unsuspend</Button>
                ) : (
                  <Button variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" onClick={handleSuspend} disabled={actionLoading}><Ban className="w-4 h-4 mr-2" />Suspend</Button>
                )}
                {!user.is_admin && <Button variant="outline" onClick={handleMakeAdmin} disabled={actionLoading}><Shield className="w-4 h-4 mr-2" />Make Admin</Button>}
                <Button variant="outline" onClick={handleSendPasswordReset} disabled={actionLoading}><KeyRound className="w-4 h-4 mr-2" />Reset Password</Button>
                <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}><Trash2 className="w-4 h-4 mr-2" />Delete</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Teams" value={teams?.length || 0} icon={Users} color="indigo" />
        <StatCard title="Players" value={players?.length || 0} icon={Users} color="blue" />
        <StatCard title="Posts" value={stats?.posts_count || 0} icon={FileText} color="emerald" />
        <StatCard title="Events Created" value={stats?.events_created || 0} icon={Calendar} color="amber" />
      </div>

      {/* Teams */}
      <Card>
        <CardHeader><CardTitle>Teams ({teams?.length || 0})</CardTitle></CardHeader>
        <CardContent>
          {teams?.length > 0 ? (
            <div className="space-y-2">
              {teams.map((team) => (
                <Link key={team.team_id} to={`/teams/${team.team_id}`} className="block p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <p className="font-medium text-gray-900">{team.name}</p>
                  <p className="text-sm text-gray-500 capitalize">{team.sport}</p>
                </Link>
              ))}
            </div>
          ) : <p className="text-gray-500">No teams</p>}
        </CardContent>
      </Card>

      {/* Payments */}
      <Card>
        <CardHeader><CardTitle>Payments ({invoices?.length || 0})</CardTitle></CardHeader>
        <CardContent>
          {invoices?.length > 0 ? (
            <div className="space-y-2">
              {invoices.map((invoice) => (
                <div key={invoice.invoice_id} className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">{invoice.description || 'Invoice'}</p>
                    <p className="text-sm text-gray-500">{invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : '-'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-lg">${((invoice.amount || 0) / 100).toFixed(2)}</p>
                    <Badge className={
                      invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                      invoice.status === 'refunded' ? 'bg-gray-100 text-gray-700' : 'bg-amber-100 text-amber-700'
                    }>{invoice.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-500">No payments</p>}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogDescription>{confirmDialog?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button onClick={executeConfirmed}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
